import {useCallback} from '@wordpress/element';
import apiFetch from "@wordpress/api-fetch";
import {checkoutEventsEmitter} from '@woocommerce/blocks-checkout-events';
import {extractShippingRateParams, getRestPath} from "../utils";

export const useApiRequests = (
    {
        isExpress = false,
        paymentMethod
    }) => {

    const createOrder = useCallback(async (currentData) => {
        const {
            billing,
            shipping,
            shouldSavePayment = false
        } = currentData;
        const {
            needsShipping,
            shippingAddress
        } = shipping;
        const {
            billingAddress
        } = billing;
        try {
            const responses = await checkoutEventsEmitter.emit('ppcp_order_create');
            const eventData = responses.reduce((acc, {data}) => ({...acc, ...data}), {});
            const response = await apiFetch({
                method: 'POST',
                url: getRestPath('wc-ppcp/v1/cart/order'),
                data: {
                    payment_method: paymentMethod,
                    ...eventData,
                    [`${paymentMethod}_save_payment`]: shouldSavePayment,
                    address_provided: !isExpress && needsShipping,
                    checkout_blocks: true,
                    context: !isExpress ? 'checkout' : 'express',
                    ...(needsShipping ? {
                        shipping_first_name: shippingAddress.first_name,
                        shipping_last_name: shippingAddress.last_name,
                        shipping_address_1: shippingAddress.address_1,
                        shipping_address_2: shippingAddress.address_2,
                        shipping_postcode: shippingAddress.postcode,
                        shipping_city: shippingAddress.city,
                        shipping_state: shippingAddress.state,
                        shipping_country: shippingAddress.country
                    } : null),
                    ...{
                        billing_first_name: billingAddress.first_name,
                        billing_last_name: billingAddress.last_name,
                        billing_address_1: billingAddress.address_1,
                        billing_address_2: billingAddress.address_2,
                        billing_postcode: billingAddress.postcode,
                        billing_city: billingAddress.city,
                        billing_state: billingAddress.state,
                        billing_country: billingAddress.country,
                        billing_email: billingAddress.email,
                        billing_phone: billingAddress.phone,
                        billing_company: billingAddress.company
                    }
                }
            });
            return response;
        } catch (error) {
            throw error;
        }
    }, [
        isExpress,
        paymentMethod
    ]);

    const createVaultSetupToken = useCallback(async () => {
        const responses = await checkoutEventsEmitter.emit('ppcp_vault_token_create');
        const eventData = responses.reduce((acc, {data}) => ({...acc, ...data}), {});
        return apiFetch({
            method: 'POST',
            url: getRestPath('/wc-ppcp/v1/vault/setup-tokens'),
            data: {
                context: 'checkout',
                payment_method: paymentMethod,
                checkout_blocks: true
            }
        }).then(token => {
            return token.id;
        }).catch(error => {
            console.info('Error creating setup token. ', error);
            throw error;
        })
    }, [
        paymentMethod
    ]);

    const onShippingChange = useCallback((data, currentData) => {
        const {shipping} = currentData;
        const {
            setSelectedRates
        } = shipping;

        const {
            address,
            shipping_method
        } = data;

        return apiFetch({
            method: 'POST',
            url: getRestPath('wc-ppcp/v1/cart/shipping'),
            data: {
                order_id: data.orderId,
                address,
                shipping_method,
                payment_method: paymentMethod
            }
        }).then(response => {
            return response;
        }).catch(error => {
            throw error;
        }).finally(() => {
            if (shipping_method) {
                setSelectedRates(...extractShippingRateParams(shipping_method))
            }
        })
    }, [
        paymentMethod
    ]);

    const createPaymentToken = useCallback((setupTokenId, data = {}) => {
        return apiFetch({
            method: 'POST',
            path: `/wc-ppcp/v1/vault/payment-tokens`,
            data: {
                setup_token: setupTokenId,
                ...data
            }
        });
    }, []);

    return {
        createOrder,
        onShippingChange,
        createPaymentToken,
        createVaultSetupToken
    }
}